import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitTheme,
    DropdownMenu,
    Attachment,
    TextInput,
    Separator,
    Composer,
    Tooltip,
    Sidebar,
    Avatar,
    Button,
    Badge,
    Item,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ListFilter,
    ArrowLeft,
    Ellipsis,
    FileText,
    Sparkles,
    Archive,
    Forward,
    PenLine,
    Trash2,
    Search,
    Clock,
    Reply,
    Inbox,
    Send,
    Star,
    File
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type Mail = {
    labels: Array<number>;
    unread: boolean;
    id: string;
};

type Folder = {
    icon: NCoreUIKitIcon;
    count?: number;
    id: string;
};

const ListFilterIcon = createKitIcon(ListFilter);

const EllipsisIcon = createKitIcon(Ellipsis);

const ArrowLeftIcon = createKitIcon(ArrowLeft);

const FileTextIcon = createKitIcon(FileText);

const SparklesIcon = createKitIcon(Sparkles);

const ArchiveIcon = createKitIcon(Archive);

const ForwardIcon = createKitIcon(Forward);

const Trash2Icon = createKitIcon(Trash2);

const SearchIcon = createKitIcon(Search);

const PenLineIcon = createKitIcon(PenLine);

const ClockIcon = createKitIcon(Clock);

const ReplyIcon = createKitIcon(Reply);

const SendIcon = createKitIcon(Send);

const StarIcon = createKitIcon(Star);

const InboxIcon = createKitIcon(Inbox);

const FileIcon = createKitIcon(File);

const FOLDERS: Array<Folder> = [
    {
        icon: InboxIcon,
        id: "inbox",
        count: 24
    },
    {
        icon: StarIcon,
        id: "starred"
    },
    {
        icon: ClockIcon,
        id: "snoozed"
    },
    {
        icon: SendIcon,
        id: "sent"
    },
    {
        icon: FileIcon,
        id: "drafts",
        count: 3
    },
    {
        icon: ArchiveIcon,
        id: "archive"
    },
    {
        icon: Trash2Icon,
        id: "trash"
    }
];

const LABELS = [
    {
        id: "customer",
        count: 12
    },
    {
        id: "finance",
        count: 4
    },
    {
        id: "team",
        count: 7
    },
    {
        id: "supply",
        count: 2
    },
    {
        id: "personal"
    }
];

const FIRST_MAIL: Mail = {
    unread: true,
    labels: [
        0,
        3
    ],
    id: "m1"
};

const MAILS: Array<Mail> = [
    {
        unread: true,
        labels: [
            0,
            3
        ],
        id: "m1"
    },
    {
        unread: true,
        labels: [
            2
        ],
        id: "m2"
    },
    {
        unread: true,
        labels: [],
        id: "m3"
    },
    {
        unread: false,
        labels: [
            1
        ],
        id: "m4"
    },
    {
        unread: false,
        labels: [],
        id: "m5"
    },
    {
        unread: false,
        labels: [
            1
        ],
        id: "m6"
    },
    {
        unread: false,
        labels: [
            4
        ],
        id: "m7"
    },
    {
        unread: false,
        labels: [],
        id: "m8"
    }
];

const SORTS = [
    "newest",
    "oldest"
];

const FILES = [
    {
        name: "teklif-q4.pdf",
        icon: FileTextIcon,
        id: "quote"
    },
    {
        name: "teslim-takvimi.xlsx",
        icon: FileTextIcon,
        id: "calendar"
    }
];

const TagDot: NCoreUIKitIcon = ({
    customColor
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    return <View
        style={{
            borderRadius: shapeConfigs.chartDotSize / 2,
            height: shapeConfigs.chartDotSize,
            width: shapeConfigs.chartDotSize,
            backgroundColor: customColor
        }}
        aria-hidden={true}
    />;
};

const stylesheet = StyleSheet.create({
    reading: {
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    list: {
        flexDirection: "column",
        flexShrink: 0
    },
    single: {
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    bar: {
        alignItems: "center",
        flexDirection: "row"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    tags: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    files: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    page: {
        flexDirection: "row"
    },
    article: {
        alignSelf: "center",
        width: "100%"
    },
    grow: {
        flexShrink: 1,
        flexGrow: 1
    },
    file: {
        minWidth: 0,
        flex: 1
    }
});

const handlePress = () => undefined;

const Inbox01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        height,
        width
    } = useWindowDimensions();

    const [
        removedIds,
        setRemovedIds
    ] = useState<Array<string>>([]);

    const [
        readIds,
        setReadIds
    ] = useState<Array<string>>([]);

    const [
        selectedId,
        setSelectedId
    ] = useState("m1");

    const [
        isReadingOpen,
        setIsReadingOpen
    ] = useState(false);

    const [
        activeFolder,
        setActiveFolder
    ] = useState("inbox");

    const [
        filter,
        setFilter
    ] = useState("all");

    const [
        sort,
        setSort
    ] = useState("newest");

    const [
        query,
        setQuery
    ] = useState("");

    const [
        sentCount,
        setSentCount
    ] = useState(0);

    const isSinglePane = width < shapeConfigs.wideBreakpoint;

    const isListVisible = !isSinglePane || !isReadingOpen;

    const isReadingVisible = !isSinglePane || isReadingOpen;

    const isUnread = (mail: Mail) => mail.unread && !readIds.includes(mail.id);

    const unreadCount = MAILS.filter((mail) => isUnread(mail) && !removedIds.includes(mail.id)).length;

    const visibleMails = MAILS.filter((mail) => {
        const isMatchFilter = filter === "all" || isUnread(mail);

        const text = `${t(`blocks.inbox01.${mail.id}.sender`)} ${t(`blocks.inbox01.${mail.id}.subject`)}`.toLowerCase();

        return !removedIds.includes(mail.id) && isMatchFilter && text.includes(query.trim().toLowerCase());
    });

    const orderedMails = sort === "oldest" ? [...visibleMails].reverse() : visibleMails;

    const selected = MAILS.find((mail) => mail.id === selectedId) ?? FIRST_MAIL;

    const isSelectedVisible = !removedIds.includes(selected.id);

    const tagColor = (index: number) => colors.chart.categorical[String(index + 1) as keyof typeof colors.chart.categorical];

    const handleFolderPress = (id: string) => () => setActiveFolder(id);

    const handleSortChange = (value: string) => setSort(value);

    const handleBackPress = () => setIsReadingOpen(false);

    const handleRemovePress = () => {
        const next = visibleMails.find((mail) => mail.id !== selected.id);

        setRemovedIds((current) => [
            ...current,
            selected.id
        ]);

        if(next) {
            setSelectedId(next.id);
        }

        setIsReadingOpen(false);
    };

    const handleSubmit = () => setSentCount(sentCount + 1);

    const renderTag = (index: number) => <Badge
        title={t(`blocks.inbox01.${LABELS[index]?.id}`)}
        icon={(props) => <TagDot
            {...props}
            customColor={tagColor(index)}
        />}
        variant="outline"
        type="neutral"
        size="small"
        key={index}
    />;

    const renderFolder = (folder: Folder) => <Sidebar.MenuItem
        key={folder.id}
    >
        <Sidebar.MenuButton
            title={t(`blocks.inbox01.${folder.id}`)}
            onPress={handleFolderPress(folder.id)}
            isActive={activeFolder === folder.id}
            icon={folder.icon}
        />
        {
            folder.count ?
                <Sidebar.MenuBadge
                    count={folder.count}
                />
            :
                null
        }
    </Sidebar.MenuItem>;

    const renderLabel = (label: typeof LABELS[number], index: number) => <Sidebar.MenuItem
        key={label.id}
    >
        <Sidebar.MenuButton
            title={t(`blocks.inbox01.${label.id}`)}
            renderIcon={() => <TagDot
                customColor={tagColor(index)}
                color="mid"
                size={0}
            />}
            onPress={handlePress}
        />
        {
            label.count ?
                <Sidebar.MenuBadge
                    count={label.count}
                />
            :
                null
        }
    </Sidebar.MenuItem>;

    const renderMail = (mail: Mail) => {
        const sender = t(`blocks.inbox01.${mail.id}.sender`);

        const unread = isUnread(mail);

        const handleMailPress = () => {
            setSelectedId(mail.id);

            setReadIds((current) => [
                ...current,
                mail.id
            ]);

            setIsReadingOpen(true);
        };

        return <Item
            isSelected={isSelectedVisible && mail.id === selected.id}
            onPress={handleMailPress}
            aria-label={sender}
            key={mail.id}
        >
            <Item.Media
                avatarProps={{
                    title: sender
                }}
                variant="avatar"
            />
            <Item.Content>
                <Item.Title>
                    {sender}
                </Item.Title>
                <Text
                    variant={unread ? "labelLargeSize" : "bodyMediumSize"}
                    numberOfLines={1}
                    color="high"
                >
                    {t(`blocks.inbox01.${mail.id}.subject`)}
                </Text>
                <Item.Description
                    numberOfLines={1}
                >
                    {t(`blocks.inbox01.${mail.id}.snippet`)}
                </Item.Description>
            </Item.Content>
            <Item.Actions>
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    {
                        unread ?
                            <Badge
                                title={t("blocks.inbox01.new")}
                                variant="tonal"
                                size="small"
                                type="info"
                            />
                        :
                            null
                    }
                    <Text
                        variant="labelMediumSize"
                        numberOfLines={1}
                        color="mid"
                    >
                        {t(`blocks.inbox01.${mail.id}.time`)}
                    </Text>
                </View>
            </Item.Actions>
            {
                mail.labels.length > 0 ?
                    <Item.Footer>
                        <View
                            style={[
                                stylesheet.tags,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            {mail.labels.map(renderTag)}
                        </View>
                    </Item.Footer>
                :
                    null
            }
        </Item>;
    };

    const renderList = () => <View
        style={[
            isSinglePane ? stylesheet.single : stylesheet.list,
            {
                width: isSinglePane ? undefined : shapeConfigs.cardWidth + spaces.spacingXxl
            }
        ]}
    >
        <View
            style={[
                stylesheet.bar,
                {
                    padding: spaces.spacingMd,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Sidebar.Trigger/>
            <Text
                variant="titleMediumSize"
                style={stylesheet.grow}
                color="high"
            >
                {t("blocks.inbox01.inbox")}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.inbox01.unreadCount", [
                    unreadCount
                ])}
            </Text>
        </View>
        <Separator />
        <ScrollView
            contentContainerStyle={{
                padding: spaces.spacingMd,
                gap: spaces.spacingMd
            }}
        >
            <TextInput
                accessibilityLabel={t("blocks.inbox01.search")}
                placeholder={t("blocks.inbox01.search")}
                spreadBehaviour="stretch"
                onChangeText={setQuery}
                icon={SearchIcon}
                value={query}
            />
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Tabs
                    onValueChange={setFilter}
                    style={stylesheet.grow}
                    value={filter}
                >
                    <Tabs.List
                        title={t("blocks.inbox01.filter")}
                    >
                        <Tabs.Trigger
                            title={t("blocks.inbox01.all")}
                            value="all"
                        />
                        <Tabs.Trigger
                            title={t("blocks.inbox01.unread")}
                            count={unreadCount}
                            value="unread"
                        />
                    </Tabs.List>
                </Tabs>
                <DropdownMenu
                    title={t("blocks.inbox01.sortTitle")}
                >
                    <DropdownMenu.Trigger>
                        <Button
                            accessibilityLabel={t("blocks.inbox01.sortTitle")}
                            icon={ListFilterIcon}
                            onPress={handlePress}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                        <DropdownMenu.RadioGroup
                            title={t("blocks.inbox01.sortTitle")}
                            onChange={handleSortChange}
                            value={sort}
                        >
                            {
                                SORTS.map((key) => <DropdownMenu.RadioItem
                                    title={t(`blocks.inbox01.${key}`)}
                                    value={key}
                                    key={key}
                                />)
                            }
                        </DropdownMenu.RadioGroup>
                    </DropdownMenu.Content>
                </DropdownMenu>
            </View>
            <Item.Group>
                {orderedMails.map(renderMail)}
            </Item.Group>
            {
                orderedMails.length === 0 ?
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.inbox01.empty")}
                    </Text>
                :
                    null
            }
        </ScrollView>
    </View>;

    const renderToolButton = (title: string, icon: NCoreUIKitIcon, onPress: () => void) => <Tooltip
        title={title}
    >
        <Button
            accessibilityLabel={title}
            onPress={onPress}
            variant="ghost"
            type="neutral"
            icon={icon}
        />
    </Tooltip>;

    const renderFile = (file: typeof FILES[number]) => <Attachment
        description={t(`blocks.inbox01.${file.id}Meta`)}
        style={stylesheet.file}
        onPress={handlePress}
        title={file.name}
        icon={FileText}
        key={file.id}
    />;

    const renderBody = () => {
        if(selected.id === "m1") {
            return <>
                <Text
                    variant="readingMediumSize"
                    color="high"
                >
                    {t("blocks.inbox01.m1.p1")}
                </Text>
                <Text
                    variant="readingMediumSize"
                    color="high"
                >
                    {t("blocks.inbox01.m1.p2")}
                </Text>
                <Text
                    variant="readingMediumSize"
                    color="high"
                >
                    {t("blocks.inbox01.m1.p3")}
                </Text>
            </>;
        }

        return <Text
            variant="readingMediumSize"
            color="high"
        >
            {t(`blocks.inbox01.${selected.id}.snippet`)}
        </Text>;
    };

    const renderReading = () => <View
        style={stylesheet.reading}
    >
        <View
            style={[
                stylesheet.bar,
                {
                    padding: spaces.spacingMd,
                    gap: spaces.spacingXs
                }
            ]}
        >
            {
                isSinglePane ?
                    <Button
                        accessibilityLabel={t("blocks.inbox01.back")}
                        onPress={handleBackPress}
                        icon={ArrowLeftIcon}
                        variant="ghost"
                        type="neutral"
                    />
                :
                    null
            }
            {renderToolButton(t("blocks.inbox01.archiveAction"), ArchiveIcon, handleRemovePress)}
            {renderToolButton(t("blocks.inbox01.delete"), Trash2Icon, handleRemovePress)}
            {renderToolButton(t("blocks.inbox01.snooze"), ClockIcon, handlePress)}
            {renderToolButton(t("blocks.inbox01.reply"), ReplyIcon, handlePress)}
            {renderToolButton(t("blocks.inbox01.forward"), ForwardIcon, handlePress)}
            <View
                style={stylesheet.grow}
            />
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.inbox01.position", [
                    String(MAILS.findIndex((mail) => mail.id === selected.id) + 1)
                ])}
            </Text>
            {renderToolButton(t("blocks.inbox01.more"), EllipsisIcon, handlePress)}
        </View>
        <Separator />
        {
            isSelectedVisible ?
                <ScrollView
                    contentContainerStyle={{
                        padding: spaces.spacingXl
                    }}
                >
                    <View
                        style={[
                            stylesheet.article,
                            {
                                maxWidth: shapeConfigs.readingMaxWidth + spaces.spacingXxl * 4,
                                gap: spaces.spacingLg
                            }
                        ]}
                    >
                        <View
                            style={{
                                gap: spaces.spacingSm
                            }}
                        >
                            <View
                                style={[
                                    stylesheet.tags,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                {selected.labels.map(renderTag)}
                            </View>
                            <Text
                                variant="headlineSmallSize"
                                color="high"
                            >
                                {t(`blocks.inbox01.${selected.id}.subject`)}
                            </Text>
                        </View>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Avatar
                                title={t(`blocks.inbox01.${selected.id}.sender`)}
                                size="small"
                            />
                            <View
                                style={stylesheet.grow}
                            >
                                <Text
                                    variant="labelLargeSize"
                                    color="high"
                                >
                                    {t(`blocks.inbox01.${selected.id}.sender`)}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t(`blocks.inbox01.${selected.id}.email`)}
                                </Text>
                            </View>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t(`blocks.inbox01.${selected.id}.date`)}
                            </Text>
                        </View>
                        <View
                            style={{
                                gap: spaces.spacingMd
                            }}
                        >
                            {renderBody()}
                            {
                                selected.id === "m1" ?
                                    <Text
                                        variant="readingMediumSize"
                                        color="high"
                                    >
                                        {t("blocks.inbox01.m1.signature")}
                                    </Text>
                                :
                                    null
                            }
                        </View>
                        {
                            selected.id === "m1" ?
                                <View
                                    style={[
                                        stylesheet.files,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    {FILES.map(renderFile)}
                                </View>
                            :
                                null
                        }
                        <View
                            style={{
                                gap: spaces.spacingSm
                            }}
                        >
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <ReplyIcon
                                    color="mid"
                                    size={16}
                                />
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.inbox01.replyTo", [
                                        t(`blocks.inbox01.${selected.id}.sender`)
                                    ])}
                                </Text>
                            </View>
                            <Composer
                                placeholder={t("blocks.inbox01.replyPlaceholder")}
                                key={`${selected.id}-${sentCount}`}
                                onSubmit={handleSubmit}
                            >
                                <Composer.Actions>
                                    <Composer.Action
                                        title={t("blocks.inbox01.suggest")}
                                        onPress={handlePress}
                                        icon={SparklesIcon}
                                    />
                                    <Composer.Action
                                        title={t("blocks.inbox01.saveDraft")}
                                        onPress={handlePress}
                                    />
                                </Composer.Actions>
                                <Composer.Submit
                                    title={t("blocks.inbox01.send")}
                                />
                            </Composer>
                        </View>
                    </View>
                </ScrollView>
            :
                <Text
                    style={{
                        padding: spaces.spacingXl
                    }}
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("blocks.inbox01.noSelection")}
                </Text>
        }
    </View>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            title={t("blocks.inbox01.navTitle")}
            collapseBehaviour="offcanvas"
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
                <Button
                    title={t("blocks.inbox01.compose")}
                    spreadBehaviour="stretch"
                    onPress={handlePress}
                    icon={PenLineIcon}
                    variant="outline"
                    type="neutral"
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group
                    title={t("blocks.inbox01.folders")}
                >
                    <Sidebar.Menu>
                        {FOLDERS.map(renderFolder)}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.inbox01.labels")}
                >
                    <Sidebar.Menu>
                        {LABELS.map(renderLabel)}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.inbox01.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.inbox01.userEmail")}
                            title={t("blocks.inbox01.userName")}
                        />
                        <Sidebar.MenuAction
                            title={t("blocks.inbox01.userMenu")}
                            onPress={handlePress}
                            isShowOnHover={false}
                            icon={EllipsisIcon}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.page,
                    {
                        height
                    }
                ]}
            >
                {isListVisible ? renderList() : null}
                {
                    isListVisible && isReadingVisible ?
                        <Separator
                            orientation="vertical"
                        />
                    :
                        null
                }
                {isReadingVisible ? renderReading() : null}
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Inbox01;
